/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Checkbox,
  checkboxClasses,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import React, { FC } from 'react';
import styled from 'styled-components';

import dayjs from 'dayjs';
import { ChecklistReportImages } from './ChecklistReportImages';
import { getProductNameByInternalCode } from '../../utils/getProductConfig';

interface VhpReportProps {
  reportData: any;
}

interface LabelProps {
  $bordered?: boolean;
  $fullsize?: boolean;
}

type RowProps = {
  $noBottomBorder?: boolean;
};

interface SectionProps {
  $topBorder?: boolean;
}

const SectionHeader: FC<{ title: string; data?: string }> = ({
  title,
  data,
}) => {
  return (
    <SectionHeaderWrapper>
      <span
        className="sectionHeader"
        style={{
          background: 'lightgrey',
          minWidth: '60%',
          width: data ? '70.2%' : '100%',
          fontSize: '18px',
          fontWeight: 700,
          paddingLeft: '1rem',
          borderBottom: '1px solid grey',
          height: '40px',
          alignContent: 'center',
        }}
      >
        {title}
      </span>
      {data && (
        <span
          style={{
            display: 'flex',
            width: '35%',
            justifyContent: 'flex-start',
            alignItems: 'center',
            textAlign: 'left',
            paddingLeft: '1rem',
            gap: '0.5rem',
            borderBottom: '1px solid grey',
            borderLeft: '1px solid grey',
            margin: 0,
          }}
        >
          <span>
            <strong>Data:</strong>
          </span>
          <span>{data}</span>
        </span>
      )}
    </SectionHeaderWrapper>
  );
};

const InputText: FC<{
  label: string;
  value?: any;
  children?: any;

  noRightBorder?: boolean;
}> = ({ label, value, children, noRightBorder }) => (
  <InputTextWrapper
    style={
      noRightBorder
        ? { borderRight: 'none' }
        : { borderRight: '1px solid grey' }
    }
  >
    <Label>{label}</Label>

    <Value style={{ minHeight: '1.5rem' }}>{value}</Value>
    {children && (
      <div style={{ display: 'flex', paddingLeft: '1rem', gap: '1rem' }}>
        {children}
      </div>
    )}
  </InputTextWrapper>
);

const LabeledCheckbox: FC<{ label: string; checked: boolean }> = ({
  label,
  checked,
}) => (
  <FormControlLabel
    control={
      <Checkbox
        size="medium"
        checked={checked}
        sx={{
          display: 'flex',
          alignContent: 'center',
          [`&, &.${checkboxClasses.checked}`]: {
            color: '#00a859',
          },
          [`&, &.${checkboxClasses.colorSecondary}`]: {
            color: '#555552',
          },
        }}
      />
    }
    label={label}
  />
);

const BooleanField: FC<{
  label: string;
  trueValue: boolean;
  falseValue: boolean;
}> = ({ label, trueValue, falseValue }) => (
  <div
    style={{
      display: 'flex',
      width: '100%',
      paddingLeft: '1rem',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}
  >
    <Label $fullsize style={{ width: '70%', borderRight: '1px solid grey' }}>
      {label}
    </Label>
    <FormGroup
      sx={{
        display: 'flex',
        flexDirection: 'row',
        width: '30%',
        justifyContent: 'center',
      }}
    >
      <LabeledCheckbox label="Sim" checked={trueValue} />
      <LabeledCheckbox label="Não" checked={falseValue} />
    </FormGroup>
  </div>
);

export const VhpChecklistReport: FC<VhpReportProps> = ({
  reportData,
}: VhpReportProps) => {
  const formattedTodaysDate = () => {
    const formattedTodayDate = dayjs().format('DD/MM/YYYY');

    return formattedTodayDate;
  };

  return (
    <Container id="report-content">
      <DataWrapper>
        <div className="page">
          <Title>Checklist de Veículo - Transporte de VHP</Title>

          <div
            style={{
              height: '0',
              borderBottom: '2px solid black',
              paddingTop: '1rem',
            }}
          ></div>

          {/* Identificacao */}
          <Section className="section-top-border">
            <SectionHeader
              title="1 - IDENTIFICAÇÃO"
              data={formattedTodaysDate()}
            />
            <Row>
              <InputText label="Nome do Motorista:" value={reportData.nome} />

              <InputText label="Celular:" value={reportData.celular} />

              <InputText
                label="CPF:"
                value={reportData.cpfmotorista}
                noRightBorder
              />
            </Row>

            <Row>
              <InputText label="Transportadora:" value={reportData.cnpj} />
              <InputText
                label="Cliente/Destino:"
                value={reportData.clienteDestino}
              />
              <InputText
                label="Produto:"
                value={getProductNameByInternalCode(reportData.produto)}
                noRightBorder
              />
            </Row>

            <Row>
              <InputText label="Placa Cavalo:" value={reportData.placacavalo} />
              <InputText
                label="Placa(s) Carreta(s):"
                value={
                  reportData.placa2 !== '' && reportData.placa2 !== null
                    ? `${reportData.placa} - ${reportData.placa2}`
                    : reportData.placa
                }
                noRightBorder
              />
            </Row>
          </Section>
          {/* Documentacao */}
          <Section $topBorder>
            <SectionHeader title="2- DOCUMENTAÇÃO" />
            <Row>
              <BooleanField
                label="Dnit ?"
                trueValue={reportData.dnit}
                falseValue={!reportData.dnit}
              />
            </Row>

            <Row>
              <BooleanField
                label="Carteira Nacional de Habilitação (CNH) ?"
                trueValue={reportData.cnh}
                falseValue={!reportData.cnh}
              />
            </Row>
          </Section>

          {/* Motorista */}
          <Section className="section">
            <SectionHeader title="3 - MOTORISTA" />

            <Row>
              <BooleanField
                label="Epis?"
                trueValue={reportData.epis}
                falseValue={!reportData.epis}
              />
            </Row>

            <Row>
              <BooleanField
                label="Motorista com roupas apropriadas ? Camisa com manga, calcado fechado, calca comprida?"
                trueValue={reportData.roupasApropriadas}
                falseValue={!reportData.roupasApropriadas}
              />
            </Row>

            <Row>
              <BooleanField
                label="Há acompanhantes com o motorista?"
                trueValue={reportData.acompanhantes}
                falseValue={!reportData.acompanhantes}
              />
            </Row>

            <Row>
              <BooleanField
                label="Motorista esta alertado sobre nao fazer uso de qualquer liquido, para vedar vazamentos na cacamba ou carroceria?"
                trueValue={reportData.alertaVazamentos}
                falseValue={!reportData.alertaVazamentos}
              />
            </Row>

            <Row>
              <BooleanField
                label="Motorista esta alertado sobre nao se alimentar no setor de armazenagem?"
                trueValue={reportData.alertaAlimentacao}
                falseValue={!reportData.alertaAlimentacao}
              />
            </Row>

            <Row>
              <BooleanField
                label="Motorista alertado que e proibido o uso de som alto dentro das dependencias da usina?"
                trueValue={reportData.alertaSomAlto}
                falseValue={!reportData.alertaSomAlto}
              />
            </Row>
          </Section>

          {/* Veiculo */}
          <Section className="section" $topBorder>
            <SectionHeader title="4 - VEÍCULO" />
            <Row>
              <BooleanField
                label="Para-choques danificados?"
                trueValue={reportData.paraChoques}
                falseValue={!reportData.paraChoques}
              />
            </Row>

            <Row>
              <BooleanField
                label="Placas danificadas?"
                trueValue={reportData.placasDanificadas}
                falseValue={!reportData.placasDanificadas}
              />
            </Row>

            <Row>
              <BooleanField
                label="Faróis danificados?"
                trueValue={reportData.faroisDanificados}
                falseValue={!reportData.faroisDanificados}
              />
            </Row>

            <Row>
              <BooleanField
                label="Laterna/Setas danificadas?"
                trueValue={reportData.lanternasSetasDanificadas}
                falseValue={!reportData.lanternasSetasDanificadas}
              />
            </Row>

            <Row>
              <BooleanField
                label="Extintor de incendio?"
                trueValue={reportData.extintorIncendio}
                falseValue={!reportData.extintorIncendio}
              />
            </Row>

            <Row>
              <BooleanField
                label="Vazamento de combustivel?"
                trueValue={reportData.vazamentoCombustivel}
                falseValue={!reportData.vazamentoCombustivel}
              />
            </Row>

            <Row>
              <BooleanField
                label="Fitas refletivas?"
                trueValue={reportData.fitasRefletivas}
                falseValue={!reportData.fitasRefletivas}
              />
            </Row>

            <Row>
              <BooleanField
                label="Pneus em bom estado?"
                trueValue={reportData.pneusBomEstado}
                falseValue={!reportData.pneusBomEstado}
              />
            </Row>

            <Row>
              <BooleanField
                label="Galoes?"
                trueValue={reportData.galoes}
                falseValue={!reportData.galoes}
              />
            </Row>
          </Section>
        </div>

        <div className="page-two page">
          {/* Cacamba/Carroceria */}
          <Section className="section" $topBorder>
            <SectionHeader title="5 - CAÇAMBA/CARROCERIA" />

            <Row>
              <BooleanField
                label="Buracos ?"
                trueValue={reportData.buracos}
                falseValue={!reportData.buracos}
              />
            </Row>

            <Row>
              <BooleanField
                label="Odor estranho?"
                trueValue={reportData.odorEstranho}
                falseValue={!reportData.odorEstranho}
              />
            </Row>

            <Row>
              <BooleanField
                label="Presença de pragas?"
                trueValue={reportData.presencaPragas}
                falseValue={!reportData.presencaPragas}
              />
            </Row>

            <Row>
              <BooleanField
                label="Residuos de carga?"
                trueValue={reportData.residuoCarga}
                falseValue={!reportData.residuoCarga}
              />
            </Row>

            <Row>
              <BooleanField
                label="Umidade?"
                trueValue={reportData.umidade}
                falseValue={!reportData.umidade}
              />
            </Row>

            <Row>
              <BooleanField
                label="Limpa?"
                trueValue={reportData.limpo}
                falseValue={!reportData.limpo}
              />
            </Row>
          </Section>

          {/* Lona */}
          <Section $topBorder>
            <SectionHeader title="6 - LONA" />

            <Row>
              <BooleanField
                label="Com resíduos?"
                trueValue={reportData.lonaComResiduos}
                falseValue={!reportData.lonaComResiduos}
              />
            </Row>

            <Row>
              <BooleanField
                label="Danificada?"
                trueValue={reportData.lonaDanificada}
                falseValue={!reportData.lonaDanificada}
              />
            </Row>

            <Row>
              <BooleanField
                label="Limpa?"
                trueValue={reportData.lonaLimpa}
                falseValue={!reportData.lonaLimpa}
              />
            </Row>

            <Row>
              <BooleanField
                label="Seca?"
                trueValue={reportData.lonaSeca}
                falseValue={!reportData.lonaSeca}
              />
            </Row>

            <Row>
              <BooleanField
                label="Odor estranho?"
                trueValue={reportData.lonaOdorEstranho}
                falseValue={!reportData.lonaOdorEstranho}
              />
            </Row>

            <Row>
              <BooleanField
                label="Presenca de pragas?"
                trueValue={reportData.lonaPresencaPragas}
                falseValue={!reportData.lonaPresencaPragas}
              />
            </Row>

            <Row>
              <BooleanField
                label="Resíduos de carga?"
                trueValue={reportData.lonaResiduoCarga}
                falseValue={!reportData.lonaResiduoCarga}
              />
            </Row>
          </Section>

          {/* Outros */}
          <Section $topBorder>
            <SectionHeader title="7 - OUTROS" />
            <Row>
              <InputText label="Lacres:" value={reportData.lacres} />

              <InputText label="Observações:" value={reportData.observacoes} />
            </Row>

            <Row>
              <InputText
                label="Responsável pela Inspeção:"
                value={reportData.responsavel}
              />

              <InputText
                label="Horário da Inspeção:"
                value={reportData.horarioInspecao}
                noRightBorder
              />
            </Row>
          </Section>

          {/* Imagens Checklist */}
          <Section $topBorder>
            <SectionHeader title="7 - Imagens" />

            {!reportData.images && (
              <Row $noBottomBorder>
                <span style={{ padding: '0.5rem 0 0.5rem 1rem' }}>
                  Nenhuma imagem adicionada.
                </span>
              </Row>
            )}

            <ChecklistReportImages
              images={reportData.images}
              imagesPerRow={2}
              startOnNewPage
            />
          </Section>
        </div>

        <div className="page-three">
          {/* Declaracoes do motorista */}
          <Section $topBorder>
            <SectionHeader title="8 - DECLARAÇÕES DO MOTORISTA" />

            <TextSection>
              <Row $noBottomBorder>
                <span style={{ paddingTop: '1rem' }}>
                  Declaro estar ciente da obrigação ao cumprimento dos itens
                  abaixo:
                </span>
              </Row>
              <Row $noBottomBorder>
                <span>
                  <strong>SEGURANÇA:</strong> É necessário a utilização do cinto
                  de segurança, conectado a linha de vida, caso tenha que subir
                  na carroceria e não tenha proteção coletiva contra queda.
                </span>
              </Row>

              <Row $noBottomBorder>
                <span>
                  <strong>SEGURANÇA DO ALIMENTO:</strong> É proibido o manuseio,
                  descarte ou consumo de alimento a partir de sua entrada na
                  portaria industrial.
                </span>
              </Row>

              <Row $noBottomBorder>
                {' '}
                <p>
                  <strong>REGISTRO E DIVULGAÇÃO DE IMAGEM:</strong> É proibido
                  fotografar, filmar e/ou divulgar colaboradores, eventos,
                  atividades laborais, processo operacionais, produtos e
                  estruturas da Bom Sucesso Agroindústria.
                </p>
              </Row>
              <Row $noBottomBorder>
                {' '}
                <p>
                  Atesto para os devidos fins, que todos os itens deste
                  checklist foram apresentados por mim durante a vistoria do
                  veículo nas dependências da Bom Sucesso Agroindústria S.A., e
                  que os mesmos acompanharão até o destino final.
                </p>
              </Row>
            </TextSection>
            <Row style={{ marginTop: '2.5rem' }}>
              <div
                style={{
                  display: 'flex',
                  width: '30%',
                  borderTop: '2px solid black',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  margin: '0 auto',
                  marginBottom: '2.5rem',
                }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: '18px',
                    marginTop: '1rem',
                  }}
                >
                  Assinatura do Motorista
                </span>
              </div>
            </Row>
          </Section>

          <Footer>
            <span style={{ fontWeight: 700, fontSize: '18px' }}>
              Bom Sucesso Agroindústria S.A
            </span>
            <span style={{}}>
              Rodovia GO-210, KM 335,1 – Zona Rural, Goiatuba, GO – CEP
              75600-000 – Telefone: (64) 3051-5090
            </span>
          </Footer>
        </div>
      </DataWrapper>
    </Container>
  );
};

const Container = styled.div`
  display: none;

  @media print {
    display: block;
    visibility: visible;

    position: absolute;
    top: 0;
    left: 0;

    width: 100%;
    margin: 0 auto;
    padding-top: 10mm;

    background: white;

    .MuiTouchRipple-root {
      display: none !important;
    }

    .page {
      break-after: page;
    }

    .page:last-of-type {
      break-after: auto;
    }

    .section-top-border {
      border-top: 1px solid grey;
      margin-top: 1rem;
    }

    .section {
      border-left: 1px solid grey;
    }
  }
`;

const Title = styled.h1`
  font-size: 28px;
  padding-left: 1rem;

  margin: 0;
`;

const DataWrapper = styled.div``;

const Section = styled.div<SectionProps>`
  display: block;

  border-left: 1px solid grey;
  border-right: 1px solid grey;

  ${({ $topBorder }) =>
    $topBorder &&
    `
      border-top: 1px solid grey;
    `}
`;

const SectionHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const InputTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 300px;
  width: 100%;
  padding: 0.25rem 0 0.25rem 1rem;
`;

const Label = styled.span<LabelProps>`
  font-size: 1rem;
  font-weight: 600;
  height: ${({ $fullsize }) => ($fullsize ? '100%' : 'auto')};
  align-content: ${({ $fullsize }) => ($fullsize ? 'center' : 'flex-start')};
`;

const Value = styled.span`
  padding-left: 1rem;
  min-height: '57px';
`;

const Row = styled.div<RowProps>`
  display: flex;

  justify-content: space-between;
  border-bottom: ${({ $noBottomBorder }) =>
    $noBottomBorder ? 'none' : '1px solid grey'};
`;

const TextSection = styled.div`
  padding-left: 1rem;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  justify-content: center;
  align-items: center;

  margin-top: 3rem;
`;
